import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Mail, ArrowRight, Phone, Lock, Shield, Users, Truck, Clock, AlertCircle, ArrowLeft } from 'lucide-react';
import { authService } from '../services';
import rateLimiter from '../utils/rateLimiting';
import { validateLoginForm, formatPhoneNumber, cleanPhoneNumber } from '../utils/validationSchemas';
import CountryCodeSelector from '../components/ui/CountryCodeSelector';
import { getCurrentPort, getExpectedDashboardForRole, getPortForDashboard, getPortNames } from '../utils/portUtils';

const SuperAdminLoginPage = ({ dashboardType = 'admin' }) => {
  const navigate = useNavigate();
  const [loginOption, setLoginOption] = useState('email');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    phone_country_code: '+91', // Default to India
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [errorTimeout, setErrorTimeout] = useState(null);
  const [errorCountdown, setErrorCountdown] = useState(0);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (errorTimeout) {
        clearTimeout(errorTimeout);
      }
    };
  }, [errorTimeout]);

  // Function to clear errors with auto-hide
  const clearErrorsWithTimeout = useCallback(() => {
    // Clear existing timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
    }
    
    // Start countdown
    setErrorCountdown(7);
    
    // Set new timeout to clear errors after 7 seconds
    const timeout = setTimeout(() => {
      setErrors({});
      setRateLimitInfo(null);
      setErrorCountdown(0);
    }, 7000);
    
    setErrorTimeout(timeout);
    
    // Update countdown every second
    const countdownInterval = setInterval(() => {
      setErrorCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [errorTimeout]);

  // Memoized input change handler
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Clean phone number input
    const processedValue = name === 'phone' ? cleanPhoneNumber(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear rate limit info
    if (rateLimitInfo) {
      setRateLimitInfo(null);
    }
    
    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
      setErrorCountdown(0);
    }
  }, [errors, rateLimitInfo, errorTimeout]);

  // Memoized form validation using Yup or fallback
  const validateForm = useCallback(async () => {
    const validationData = {
      ...formData,
      [loginOption]: formData[loginOption]
    };
    
    const { errors: validationErrors, isValid } = await validateLoginForm(loginOption, validationData);
    setErrors(validationErrors);
    return isValid;
  }, [formData, loginOption]);

  // Memoized form submission with rate limiting
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validate form first
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }
    
    const contact = loginOption === 'email' ? formData.email : formData.phone;
    
    // Check rate limiting
    const rateLimitCheck = rateLimiter.checkRateLimit(contact);
    if (!rateLimitCheck.allowed) {
      setRateLimitInfo(rateLimitCheck);
      setErrors({ 
        general: rateLimitCheck.message || 'Too many attempts. Please try again later.' 
      });
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    setRateLimitInfo(null);
    
    try {
      // Try direct login first (without OTP)
      const loginResult = await authService.login({
        contact,
        password: formData.password,
        loginType: loginOption
      });
      
      if (loginResult.success) {
        // Record successful attempt (reset rate limiting)
        rateLimiter.recordSuccess(contact);
        
        // Check port compatibility for direct login
        if (loginResult.data.user && loginResult.data.user.role) {
          const currentPort = getCurrentPort();
          const expectedDashboard = getExpectedDashboardForRole(loginResult.data.user.role);
          const expectedPort = getPortForDashboard(expectedDashboard);
          
          
          // If user is on wrong port, show error and don't proceed
          if (currentPort !== expectedPort) {
            const portNames = getPortNames();
            
            const currentPortName = portNames[currentPort] || `Port ${currentPort}`;
            const expectedPortName = portNames[expectedPort] || `Port ${expectedPort}`;
            
            setErrors({
              general: `Access denied. ${loginResult.data.user.role} users must login from ${expectedPortName} (Port ${expectedPort}). You are currently on ${currentPortName}.`
            });
            clearErrorsWithTimeout();
            return;
          }
        }
        
        // Navigate to super admin dashboard
        navigate('/super-admin/dashboard');
        return;
      }
    } catch (loginError) {
      // If direct login fails, try OTP flow
      try {
        const result = await authService.sendLoginOtp({
          contact,
          password: formData.password,
          loginType: loginOption
        });
        
        if (result.success) {
          // Record successful attempt (reset rate limiting)
          rateLimiter.recordSuccess(contact);
          
          // Check port compatibility BEFORE navigating to OTP page
          if (result.data.userRole) {
            const currentPort = getCurrentPort();
            const expectedDashboard = getExpectedDashboardForRole(result.data.userRole);
            const expectedPort = getPortForDashboard(expectedDashboard);
            
            
            // If user is on wrong port, show error and don't proceed to OTP
            if (currentPort !== expectedPort) {
              const portNames = getPortNames();
              
              const currentPortName = portNames[currentPort] || `Port ${currentPort}`;
              const expectedPortName = portNames[expectedPort] || `Port ${expectedPort}`;
              
              setErrors({
                general: `Access denied. ${result.data.userRole} users must login from ${expectedPortName} (Port ${expectedPort}). You are currently on ${currentPortName}.`
              });
              clearErrorsWithTimeout();
              return;
            }
          }
          
          navigate('/otp', { 
            state: { 
              contact,
              password: formData.password,
              loginOption: loginOption,
              userId: result.data.userId
            } 
          });
          return;
        }
      } catch (otpError) {
        // Record failed attempt for rate limiting
        rateLimiter.recordFailedAttempt(contact);
        
        // Check if it's an authentication error (invalid credentials)
        if (otpError.message && (
          otpError.message.includes('Invalid') || 
          otpError.message.includes('User not found') ||
          otpError.message.includes('password') ||
          otpError.message.includes('email')
        )) {
          setErrors({ 
            general: otpError.message || 'Invalid email/phone or password. Please check your credentials and try again.' 
          });
        } else {
          setErrors({ 
            general: otpError.message || 'Failed to login. Please try again.' 
          });
        }
        
        // Auto-hide error after 7 seconds
        clearErrorsWithTimeout();
      }
    } finally {
      setIsLoading(false);
    }
  }, [formData, loginOption, validateForm, navigate, clearErrorsWithTimeout]);

  // Memoized login option change handler
  const handleLoginOptionChange = useCallback((option) => {
    setLoginOption(option);
    setFormData({ email: '', phone: '', password: '' });
    setErrors({});
    setRateLimitInfo(null);
    
    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
      setErrorCountdown(0);
    }
  }, [errorTimeout]);

  const features = [
    {
      icon: Shield,
      title: 'Super Admin Access',
      description: 'Full platform control and organization management'
    },
    {
      icon: Users,
      title: 'Organization Management',
      description: 'Register and manage hospitals and fleet operators'
    },
    {
      icon: Truck,
      title: 'Platform Oversight',
      description: 'Monitor all operations and system performance'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Side - Features & Branding */}
          <div className="hidden lg:block space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-[#1e3a8a] rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Super Admin Portal</h1>
              </div>
              <p className="text-lg font-semibold text-gray-600 max-w-md">
                Access the Super Admin dashboard to manage the entire Smart Ambulance platform.
              </p>
            </div>

            <div className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto lg:max-w-none">
            <Card className="shadow-xl rounded-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-2 text-center pb-8">
                <div className="flex items-center justify-center space-x-3 mb-4 lg:hidden">
                  <div className="w-10 h-10 bg-[#1e3a8a] rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">Super Admin Portal</h1>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">Super Admin Login</CardTitle>
                <CardDescription className="text-gray-600">
                  Sign in with your Super Admin credentials
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Error Display */}
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                        <p className="text-sm text-red-600">{errors.general}</p>
                      </div>
                      {errorCountdown > 0 && (
                        <div className="flex items-center text-xs text-red-500">
                          <Clock className="w-3 h-3 mr-1" />
                          <span>Auto-hide in {errorCountdown}s</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Rate Limiting Display */}
                {rateLimitInfo && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                      <div>
                        <p className="text-sm text-yellow-800 font-medium">Rate Limited</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          {rateLimitInfo.message}
                        </p>
                        {rateLimitInfo.lockoutTime > 0 && (
                          <p className="text-xs text-yellow-700 mt-1">
                            Try again in {Math.ceil(rateLimitInfo.lockoutTime / 60000)} minutes
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Login Option Selection */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-gray-700">Choose login method</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => handleLoginOptionChange('email')}
                        className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${
                          loginOption === 'email'
                            ? 'border-[#1e3a8a] bg-[#1e3a8a]/5 text-[#1e3a8a]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                        disabled={isLoading}
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm font-medium">Email</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLoginOptionChange('phone')}
                        className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-lg border-2 transition-all duration-200 ${
                          loginOption === 'phone'
                            ? 'border-[#1e3a8a] bg-[#1e3a8a]/5 text-[#1e3a8a]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                        disabled={isLoading}
                      >
                        <Phone className="w-4 h-4" />
                        <span className="text-sm font-medium">Phone</span>
                      </button>
                    </div>
                  </div>

                  {/* Email Field */}
                  {loginOption === 'email' && (
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-sm font-medium text-gray-700">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="text"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`pl-12 sm:pl-14 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                          disabled={isLoading}
                          placeholder="Enter your email or phone number"
                        />
                      </div>
                      {errors.email && (
                        <p className="text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>
                  )}

                  {/* Phone Field */}
                  {loginOption === 'phone' && (
                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <div className="flex gap-2">
                        <div className="w-32">
                          <CountryCodeSelector
                            value={formData.phone_country_code}
                            onChange={(e) => handleInputChange({ target: { name: 'phone_country_code', value: e.target.value } })}
                            disabled={isLoading}
                            placeholder="Country"
                          />
                        </div>
                        <div className="flex-1 relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={`pl-10 ${errors.phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                            disabled={isLoading}
                            placeholder="Enter your email or phone number"
                            maxLength={10}
                          />
                        </div>
                      </div>
                      {errors.phone && (
                        <p className="text-sm text-red-600">{errors.phone}</p>
                      )}
                      {formData.phone && formData.phone.length === 10 && (
                        <p className="text-xs text-gray-500">
                          Formatted: {formatPhoneNumber(formData.phone)}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Password Field - Always required */}
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="Enter your Super Admin password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`pl-12 sm:pl-14 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                        disabled={isLoading}
                      />
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-600">{errors.password}</p>
                    )}
                  </div>

                  {/* Login Button */}
                  <Button
                    type="submit"
                    className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white font-medium py-3 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:transform-none disabled:opacity-50"
                    disabled={isLoading || (rateLimitInfo && !rateLimitInfo.allowed)}
                  >
                    {isLoading ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending OTP...</span>
                      </div>
                    ) : rateLimitInfo && !rateLimitInfo.allowed ? (
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4" />
                        <span>Rate Limited</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Get OTP</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </Button>
                </form>

                {/* Navigation Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/login')}
                    className="flex items-center space-x-2"
                    disabled={isLoading}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Login</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => navigate('/super-admin-registration')}
                    className="flex items-center space-x-2"
                    disabled={isLoading}
                  >
                    <span>Register Organizations</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Help Text */}
                <div className="text-center pt-4">
                  <p className="text-sm text-gray-600">
                    Need help? Contact{' '}
                    <a 
                      href="mailto:support@smartambulance.com" 
                      className="text-[#1e3a8a] hover:text-blue-700 font-medium transition-colors"
                    >
                      support@smartambulance.com
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLoginPage;
